import { type FC } from "react";
import { type CassetteProps } from "@userFrontend/features/publication/components/ScoutCassette/type";
import { Salaries } from "@userFrontend/features/publication/components/Salaries";
import { Label } from "@root/shared/components/Label";
import { Icon } from "@root/shared/components/Icon";
import { Button } from "@root/shared/components/Button";
import { Link } from "@root/shared/components/Link";
import { ShopWorksModal } from "@userFrontend/features/publication/components/ShopWorksModal";
import { ModalInner } from "@userFrontend/features/publication/components/ScoutCassette/uiParts/ModalInner";
import {
  GA_SCOUT_ASSENT_ACTION,
  GA_SCOUT_EXTEND_ACTION,
} from "@userFrontend/features/publication/components/ScoutCassette/constants";
import * as styles from "./styles.css";
import { MatchReasons } from "../MatchReasons";
import { PremiumMessage } from "../PremiumMessage";

const DEFAULT_SHOW_SALARY_COUNT = 4;

export const CassettePc: FC<CassetteProps> = ({
  workCode,
  shopId,
  imgSrc,
  labels: { isShowLabel, isDecline, isNewArrivals, isPremiumScout },
  brandName,
  businessType,
  occupation,
  matchReasons,
  message,
  isShopScout,
  shopCount,
  shopName,
  detailUrl,
  title,
  employmentAndSalaryType,
  location,
  entryPath,
  acceptPath,
  deadline = 0,
  isExtendable,
  extendPath,
  handleOpenModal,
  handleCloseModal,
  handleClickLink,
  handleClickExtendScoutDeadlineButton,
  isOpenModal,
  cassettes,
  openModalButtonRef,
  isShowIndicator,
  indicatorRef,
}) => (
  <div className={styles.cassette}>
    <a href={detailUrl} className={styles.link}>
      <div className={styles.imageOuter}>
        <img src={imgSrc} alt={brandName} width={657} height={370} className={styles.image} />
      </div>
      <div className={styles.container}>
        {isShowLabel && (isDecline || isNewArrivals || isPremiumScout) && (
          <div className={styles.labelsOuter}>
            {isDecline && <Label theme="gray">辞退</Label>}
            {isNewArrivals && <Label theme="red">新着</Label>}
            {isPremiumScout && <span className={styles.premiumScoutLabel}>プレミアムスカウト</span>}
          </div>
        )}
        <p
          className={styles.brandNameOuter({
            hasBorder: !(matchReasons?.length || message?.length),
          })}
        >
          {brandName}｜{businessType} / {occupation}
        </p>
        {!isPremiumScout && matchReasons && matchReasons.length > 0 && (
          <MatchReasons matchReasons={matchReasons} className={styles.message} />
        )}
        {isPremiumScout && message && message?.length > 0 && (
          <PremiumMessage message={message} className={styles.message} />
        )}
        {isShopScout && shopCount === 1 && (
          <p className={styles.isShopScout}>この店舗からのスカウト</p>
        )}
        <p className={styles.shopName}>
          <span className={styles.shopIcon}>
            <Icon name="location" />
          </span>
          {shopName}
        </p>
        <p className={styles.title}>{title}</p>
        <div className={styles.salariesOuter}>
          <Salaries
            employmentAndSalaryType={employmentAndSalaryType.slice(0, DEFAULT_SHOW_SALARY_COUNT)}
          />
          {employmentAndSalaryType.length > DEFAULT_SHOW_SALARY_COUNT && (
            <p className={styles.otherSalary}>...他</p>
          )}
        </div>
        <p className={styles.location}>{location}</p>
        {!isShopScout && shopCount > 1 && (
          <div
            className={styles.showShopModalOuter}
            onClick={(e) => e.preventDefault()}
            onKeyDown={(e) => e.preventDefault()}
            role="button"
            tabIndex={0}
          >
            <Button
              type="button"
              theme="tertiary"
              size="small"
              rightIcon={<Icon name="arrow-bold-right" />}
              onClick={handleOpenModal}
              ref={openModalButtonRef}
            >
              <span>
                他 <span className={styles.shopCount}>{shopCount - 1}</span> 店舗の求人を見る
              </span>
            </Button>
          </div>
        )}
      </div>
    </a>
    <div className={styles.conversionArea}>
      {isDecline ? (
        <p className={styles.entryOuter}>
          <Link href={entryPath} theme="primary" className={styles.actionButton}>
            応募する
          </Link>
        </p>
      ) : (
        <>
          <p className={styles.deadline}>
            有効期限：<span className={styles.deadlineCount}>あと{deadline}日</span>
          </p>
          <p className={styles.buttonsOuter}>
            {isExtendable && extendPath ? (
              <Button
                type="button"
                theme="secondary"
                className={styles.actionButton}
                onClick={() => handleClickExtendScoutDeadlineButton(extendPath, "", "")}
                data-ga-action={GA_SCOUT_EXTEND_ACTION}
                data-ga-label={`${workCode}_${shopId}`}
              >
                期限延長
              </Button>
            ) : (
              <Button type="button" theme="disabled" className={styles.actionButton}>
                期限延長済み
              </Button>
            )}
            <Link
              href={acceptPath}
              theme="primary"
              className={styles.actionButton}
              data-ga-action={GA_SCOUT_ASSENT_ACTION}
              data-ga-label={`${workCode}_${shopId}`}
            >
              スカウトを受ける
            </Link>
          </p>
        </>
      )}
    </div>
    {cassettes && cassettes.length > 0 && (
      <ShopWorksModal
        isShow={isOpenModal}
        brandName={brandName}
        businessTypeAndOccupation={`${businessType} / ${occupation}`}
        totalNumber={shopCount}
        handleCloseModal={handleCloseModal}
      >
        <ModalInner
          cassettes={cassettes}
          isShowIndicator={isShowIndicator}
          indicatorRef={indicatorRef}
          handleClickLink={handleClickLink}
        />
      </ShopWorksModal>
    )}
    <input type="hidden" name="scout" id="scout" value="true" autoComplete="off" />
  </div>
);
